import { connect } from 'react-redux';
import { get, groupBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { closeGenericModal, openGenericModal } from '@actions/modal';
import {
  getIntegrationMappings,
  getIntegrationsWithClientConfig,
  getIntegrationsWithStoreConfig,
} from '@services/integrations';
import { getStoresOfClient } from '@services/store';
import { SearchBar } from '../../../commons/utils/styledLibraryComponents';
import { showErrorMessage, showSuccessMessage } from '@actions/messageconfirmation';
import { storeSupplierProfileMapping as storeSupplierProfileMappingService } from '@services/storeSupplierProfileMapping';
import NavigationBreadCrumb from '../../../commons/Breadcrumb/NavigationBreadCrumb';

import {
  CardsGrid,
  Container,
  ContentWrapper,
  SearchBarWrapper,
  Section,
  SectionTitle,
} from './styledComponents';
import { IntegrationCard } from './components/IntegrationCard';
import { IntegrationDetailDrawer } from './components/IntegrationDetailDrawer';
import { IntegrationsPageLoading } from './components/IntegrationsPageLoading';
import normalizeStringValue from '../../../commons/utils/normalizeStringValue';

import {
  INTEGRATION_STATUSES,
  OAUTH2_AUTH_TYPE,
  PENNYLANE_OAUTH2_CODE,
  TOKEN_AUTH_TYPE,
} from './utils/constants';
import { isOAuth2RefreshTokenStillValid } from './utils/isOAuth2RefreshTokenStillValid';

const DEFAULT_ICON_COLOR = '#8ECAE6';

const TYPE_TO_TITLE_KEY = {
  ACCOUNTING: 'ADMIN.INTEGRATIONS.CATEGORY_FACTURATION',
  HR: 'ADMIN.INTEGRATIONS.CATEGORY_RH_BI',
  ORDER_AVAILABILITY: 'ADMIN.INTEGRATIONS.CATEGORY_ORDER_AVAILABILITY',
  TRACEABILITY: 'ADMIN.INTEGRATIONS.CATEGORY_TRACEABILITY',
};

const CATEGORY_DISPLAY_ORDER = ['ACCOUNTING', 'TRACEABILITY', 'HR', 'ORDER_AVAILABILITY'];

const getCategoryDisplayIndex = (type) => {
  const index = CATEGORY_DISPLAY_ORDER.indexOf(type);

  return index === -1 ? CATEGORY_DISPLAY_ORDER.length : index;
};

const mapPartnerToCardIntegration = (partner) => {
  const name = partner.name ?? '';
  const firstLetter = name.charAt(0).toUpperCase();

  let status = null;

  if (partner.authType === OAUTH2_AUTH_TYPE && partner.config) {
    const credentials = partner.config.credentials ?? {};
    const isRefreshTokenValid = isOAuth2RefreshTokenStillValid(credentials);

    status = isRefreshTokenValid ? INTEGRATION_STATUSES.ACTIVE : INTEGRATION_STATUSES.PAUSED;
  } else if (partner.authType === TOKEN_AUTH_TYPE && partner.config) {
    const credentials = partner.config.credentials ?? {};
    const hasToken = typeof credentials.token === 'string' && credentials.token.trim().length > 0;

    status = hasToken ? INTEGRATION_STATUSES.ACTIVE : INTEGRATION_STATUSES.PAUSED;
  } else if (partner.config) {
    status = INTEGRATION_STATUSES.ACTIVE;
  }

  return {
    id: partner.id,
    name,
    description: partner.description ?? '',
    iconLetter: firstLetter || '?',
    iconColor: DEFAULT_ICON_COLOR,
    logoUrl: partner.logoUrl ?? null,
    status,
    authType: partner.authType ?? null,
    code: partner.code ?? null,
    partnerConfigId: partner.config?.id ?? null,
    requiredMappings: Array.isArray(partner.requiredMappings) ? partner.requiredMappings : [],
    savedToken: partner.authType === TOKEN_AUTH_TYPE && partner.config?.credentials?.token,
  };
};

const groupIntegrationsByType = (integrations) => {
  const byType = groupBy(integrations, 'type');

  return Object.entries(byType)
    .map(([type, items]) => ({
      type,
      titleKey: TYPE_TO_TITLE_KEY[type] ?? `ADMIN.INTEGRATIONS.CATEGORY_${type}`,
      integrations: items
        .map(mapPartnerToCardIntegration)
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())),
    }))
    .sort((a, b) => getCategoryDisplayIndex(a.type) - getCategoryDisplayIndex(b.type));
};

const filterIntegrationsBySearch = (categories, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) {
    return categories;
  }

  const term = normalizeStringValue(searchTerm.trim());

  return categories
    .map((category) => ({
      ...category,
      integrations: category.integrations.filter(
        (integration) =>
          normalizeStringValue(integration.name).includes(term) ||
          normalizeStringValue(integration.description).includes(term),
      ),
    }))
    .filter((category) => category.integrations.length > 0);
};

const findIntegrationInCategoriesByCode = (integrationCategories, partnerCode) => {
  if (!Array.isArray(integrationCategories) || !partnerCode) {
    return null;
  }

  const flattened = integrationCategories.flatMap((category) => category.integrations);

  return flattened.find((item) => item.code === partnerCode) ?? null;
};

const AdminIntegrationsComponent = ({
  closeGenericModal: dispatchCloseGenericModal,
  currentStore = null,
  hideBreadcrumb = false,
  history,
  location,
  match,
  openGenericModal: dispatchOpenGenericModal,
  showErrorMessage: dispatchShowErrorMessage,
  showSuccessMessage: dispatchShowSuccessMessage,
  user = null,
}) => {
  const isInpulseUser = Boolean(user?.isDeepsight);
  const { t } = useTranslation();
  const path = get(match, 'path');
  const [searchValue, setSearchValue] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailIntegration, setDetailIntegration] = useState(null);
  const [prefetchedStores, setPrefetchedStores] = useState([]);
  /** Pennylane OAuth mappings prefetched in loadIntegrations; do not reuse for other partners (Combo, Skello, etc.). */
  const [prefetchedMappings, setPrefetchedMappings] = useState(null);
  const [linkedStoreIds, setLinkedStoreIds] = useState(null);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  const handleCloseIntegrationDetail = useCallback(() => {
    dispatchCloseGenericModal();
    setDetailIntegration(null);
  }, [dispatchCloseGenericModal]);

  const handleOpenIntegrationDetail = useCallback((integration) => {
    setDetailIntegration(integration);
  }, []);

  const openIntegrationDetailFromPartnersList = useCallback((partnersList, partnerCode) => {
    if (!Array.isArray(partnersList) || !partnerCode) {
      return;
    }

    const partner = partnersList.find((item) => item.code === partnerCode);

    if (!partner || partner.authType !== OAUTH2_AUTH_TYPE || !partner.config) {
      return;
    }

    if (!isOAuth2RefreshTokenStillValid(partner.config.credentials ?? {})) {
      return;
    }

    setDetailIntegration(mapPartnerToCardIntegration(partner));
  }, []);

  const loadIntegrations = useCallback(
    async (options = {}) => {
      const withLoader = options.withLoader !== false;

      if (withLoader) {
        setLoading(true);
        setError(null);
      }

      try {
        const data = currentStore?.id
          ? await getIntegrationsWithStoreConfig(currentStore.id)
          : await getIntegrationsWithClientConfig();
        const rawPartnersList = Array.isArray(data) ? data : [];
        const isNonKitchenStoreContext = Boolean(currentStore?.id) && !currentStore?.isKitchen;
        const partnersList = isNonKitchenStoreContext
          ? rawPartnersList.filter((partner) => partner.code !== PENNYLANE_OAUTH2_CODE)
          : rawPartnersList;
        const grouped = groupIntegrationsByType(partnersList);

        setCategories(grouped);

        const activePennylane = partnersList.find(
          (partner) =>
            partner.code === PENNYLANE_OAUTH2_CODE &&
            partner.config &&
            isOAuth2RefreshTokenStillValid(partner.config.credentials ?? {}),
        );

        if (activePennylane) {
          const [storesOutcome, mappingsOutcome] = await Promise.allSettled([
            getStoresOfClient(),
            getIntegrationMappings(activePennylane.id, currentStore?.id ?? undefined),
          ]);

          if (storesOutcome.status === 'fulfilled') {
            const stores = storesOutcome.value;
            setPrefetchedStores(Array.isArray(stores) ? stores : []);
          }

          if (mappingsOutcome.status === 'fulfilled') {
            const mappings = mappingsOutcome.value;
            setPrefetchedMappings(Array.isArray(mappings) ? mappings : []);
          }

          if (storesOutcome.status === 'rejected') {
            setPrefetchedStores([]);
          }

          if (mappingsOutcome.status === 'rejected') {
            setPrefetchedMappings([]);
          }
        } else {
          setPrefetchedStores([]);
          setPrefetchedMappings(null);
        }

        if (withLoader) {
          setError(null);
        }

        return partnersList;
      } catch (err) {
        if (withLoader) {
          setCategories([]);
          setError(err);
        }

        return;
      } finally {
        if (withLoader) {
          setLoading(false);
        }
      }
    },
    [currentStore?.id],
  );

  const handleMappingsSaved = useCallback((payload) => {
    if (!payload || payload.partnerCode !== PENNYLANE_OAUTH2_CODE) {
      return;
    }

    setPrefetchedMappings(payload.mappings);
  }, []);

  const handleIntegrationDisconnected = useCallback(() => {
    dispatchShowSuccessMessage(t('ADMIN.INTEGRATIONS.OAUTH_DISCONNECT_SUCCESS'));
    loadIntegrations({ withLoader: false });
    setDetailIntegration(null);
  }, [dispatchShowSuccessMessage, loadIntegrations, t]);

  const handleTokenSaved = useCallback(async () => {
    dispatchShowSuccessMessage(t('ADMIN.INTEGRATIONS.TOKEN_CONNECT_SUCCESS'));

    const partnersList = await loadIntegrations({ withLoader: false });

    setDetailIntegration((previous) => {
      if (!previous || !Array.isArray(partnersList)) {
        return previous;
      }

      const updatedPartner = partnersList.find(({ id }) => id === previous.id);

      if (!updatedPartner) {
        return previous;
      }

      return mapPartnerToCardIntegration(updatedPartner);
    });
  }, [dispatchShowSuccessMessage, loadIntegrations, t]);

  useEffect(() => {
    loadIntegrations({ withLoader: true });
  }, [loadIntegrations]);

  useEffect(() => {
    if (!currentStore?.isKitchen) {
      setLinkedStoreIds(null);
      return;
    }

    const supplierId = currentStore.supplier?.[0]?.id;

    if (!supplierId) {
      setLinkedStoreIds([]);
      return;
    }

    let cancelled = false;

    const fetchLinkedStoreIds = async () => {
      try {
        const mappings =
          await storeSupplierProfileMappingService.getStoreSupplierProfileMappingsOfSupplier(
            supplierId,
          );

        if (cancelled) {
          return;
        }

        const storeIds = (Array.isArray(mappings) ? mappings : []).map(({ storeId }) => storeId);

        setLinkedStoreIds(storeIds);
      } catch {
        if (cancelled) {
          return;
        }

        setLinkedStoreIds([]);
      }
    };

    fetchLinkedStoreIds();

    return () => {
      cancelled = true;
    };
  }, [currentStore]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const integration = params.get('integration');
    const openIntegrationPartnerCode = params.get('openIntegration');

    if (!integration) {
      return;
    }

    params.delete('integration');
    params.delete('openIntegration');
    const searchString = params.toString();
    const nextUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname;

    history.replace(nextUrl);

    if (integration === 'success') {
      dispatchShowSuccessMessage(t('ADMIN.INTEGRATIONS.OAUTH_CALLBACK_SUCCESS'));

      const partnerCodeToOpen = openIntegrationPartnerCode ?? PENNYLANE_OAUTH2_CODE;

      void (async () => {
        const partnersList = await loadIntegrations({ withLoader: false });

        openIntegrationDetailFromPartnersList(partnersList, partnerCodeToOpen);
      })();
    }

    if (integration === 'error') {
      dispatchShowErrorMessage(t('ADMIN.INTEGRATIONS.OAUTH_CALLBACK_ERROR'));
    }
  }, [
    dispatchShowErrorMessage,
    dispatchShowSuccessMessage,
    history,
    loadIntegrations,
    location.pathname,
    location.search,
    openIntegrationDetailFromPartnersList,
    t,
  ]);

  useEffect(() => {
    if (loading || error) {
      return;
    }

    const params = new URLSearchParams(location.search);

    if (params.get('integration')) {
      return;
    }

    const openIntegrationCode = params.get('openIntegration');

    if (!openIntegrationCode) {
      return;
    }

    const cardIntegration = findIntegrationInCategoriesByCode(categories, openIntegrationCode);

    if (!cardIntegration) {
      return;
    }

    params.delete('openIntegration');
    const searchString = params.toString();
    const nextUrl = searchString ? `${location.pathname}?${searchString}` : location.pathname;

    history.replace(nextUrl);
    setDetailIntegration(cardIntegration);
  }, [categories, error, history, loading, location.pathname, location.search]);

  const filteredCategories = useMemo(
    () => filterIntegrationsBySearch(categories, searchValue),
    [categories, searchValue],
  );

  if (loading) {
    return (
      <>
        {!hideBreadcrumb && <NavigationBreadCrumb featurePath={path} />}
        <IntegrationsPageLoading />
      </>
    );
  }

  if (error) {
    return (
      <>
        {!hideBreadcrumb && <NavigationBreadCrumb featurePath={path} />}
        <Container>{t('ADMIN.INTEGRATIONS.FETCH_ERROR')}</Container>
      </>
    );
  }

  return (
    <>
      {!hideBreadcrumb && <NavigationBreadCrumb featurePath={path} />}
      <Container $topOffset={hideBreadcrumb}>
        <ContentWrapper>
          <SearchBarWrapper>
            <SearchBar
              disabled={false}
              maxWidth={400}
              minWidth={240}
              placeholder={t('ADMIN.INTEGRATIONS.SEARCH_PLACEHOLDER')}
              setValue={handleSearchChange}
              value={searchValue}
            />
          </SearchBarWrapper>

          {filteredCategories.map((category) => (
            <Section key={category.type}>
              <SectionTitle>{t(category.titleKey)}</SectionTitle>
              <CardsGrid>
                {category.integrations.map((integration) => (
                  <IntegrationCard
                    integration={integration}
                    key={integration.id}
                    onOpenDetail={handleOpenIntegrationDetail}
                  />
                ))}
              </CardsGrid>
            </Section>
          ))}
        </ContentWrapper>
      </Container>
      {detailIntegration && (
        <IntegrationDetailDrawer
          integration={detailIntegration}
          isInpulseUser={isInpulseUser}
          isKitchenContext={
            detailIntegration.code === PENNYLANE_OAUTH2_CODE && currentStore?.isKitchen === true
          }
          linkedStoreIds={detailIntegration.code === PENNYLANE_OAUTH2_CODE ? linkedStoreIds : null}
          openGenericModal={dispatchOpenGenericModal}
          prefetchedMappings={prefetchedMappings}
          prefetchedStores={prefetchedStores}
          returnPath={get(match, 'url', path)}
          showErrorMessage={dispatchShowErrorMessage}
          showSuccessMessage={dispatchShowSuccessMessage}
          storeId={currentStore?.id ?? null}
          onClose={handleCloseIntegrationDetail}
          onIntegrationDisconnected={handleIntegrationDisconnected}
          onMappingsSaved={handleMappingsSaved}
          onTokenSaved={handleTokenSaved}
        />
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  closeGenericModal: () => {
    dispatch(closeGenericModal());
  },
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
});

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const ConnectedAdminIntegrations = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AdminIntegrationsComponent),
);

export { ConnectedAdminIntegrations as AdminIntegrations };
export default ConnectedAdminIntegrations;
